import { useState, useEffect } from "react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { format } from "date-fns"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent } from "@/components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Checkbox } from "@/components/ui/checkbox"
import { Users, Trophy, AlertTriangle } from "lucide-react"
import { useRoundReport } from "@/graphql/hooks/useRoundReport"
import { useToast } from "@/hooks/use-toast"
import { UserAvatar } from "./ui/user-avatar"
import DOMPurify from "dompurify"

interface Player {
  id: number
  drop: number
  Person: {
    id: number
    avatar?: string
    name?: string
    fullName: string
    urlAvatar?: string
    phone?: string
    code: string
  }
}

interface Match {
  id: number
  table: number
  reported: number
  draw: number
  roundId: number
  dropPlayer: boolean
  dropOpponent: boolean
  winnerVictories: number
  loserVictories: number

  playerId: number
  opponentId: number
  Player: Player
  Opponent?: Player

  winnerId: number
  loserId: number

  modified?: Date

  ModifiedBy?: {
    id: number
    fullName: string
  }
}

interface MatchReportModalProps {
  match: Match | null
  tournamentId: number
  roundId: number
  onClose: () => void
  onSuccess: () => void
}

export const MatchReportModal = ({
  match,
  tournamentId,
  onClose,
  onSuccess,
}: MatchReportModalProps) => {
  const [playerWins, setPlayerWins] = useState<number>(0)
  const [opponentWins, setOpponentWins] = useState<number>(0)
  const [dropPlayer, setDropPlayer] = useState(false)
  const [dropOpponent, setDropOpponent] = useState(false)
  const [showResultModal, setShowResultModal] = useState(false)
  const [resultText, setResultText] = useState("")

  const isMobile = typeof window !== "undefined" && window.matchMedia("(max-width: 640px)").matches

  // Update state when match changes
  useEffect(() => {
    if (match) {
      if (match.reported) {
        // Determine which player is winner/loser based on match data
        const isPlayerWinner = match.winnerId === match.playerId
        const isOpponentWinner = match.winnerId === match.opponentId

        if (match.draw) {
          setPlayerWins(match.winnerVictories)
          setOpponentWins(match.winnerVictories) // In draws, both have same score
        } else if (isPlayerWinner) {
          setPlayerWins(match.winnerVictories)
          setOpponentWins(match.loserVictories)
        } else if (isOpponentWinner) {
          setPlayerWins(match.loserVictories)
          setOpponentWins(match.winnerVictories)
        } else {
          setPlayerWins(0)
          setOpponentWins(0)
        }
      } else {
        setPlayerWins(0)
        setOpponentWins(0)
      }
      setDropPlayer(match.dropPlayer || false)
      setDropOpponent(match.dropOpponent || false)
    }
  }, [match])

  const { roundReport, loading } = useRoundReport()
  const { toast } = useToast()

  const isByeMatch = !match?.Opponent

  // For bye matches, player always wins
  const isDraw = !isByeMatch && playerWins === opponentWins
  const winnerId = isByeMatch
    ? match?.Player.id
    : playerWins > opponentWins
      ? match?.Player.id
      : match?.Opponent?.id
  const loserId = isByeMatch
    ? 0
    : playerWins < opponentWins
      ? match?.Player.id
      : match?.Opponent?.id

  const handleSubmit = async () => {
    if (!match) return

    try {
      const result = await roundReport({
        id: match.id,
        dropOpponent,
        dropPlayer,
        roundId: match.roundId,
        table: match.table,
        tournamentId,
        winnerId: isDraw ? 0 : winnerId || 0,
        loserId: isDraw ? 0 : loserId || 0,
        draw: isDraw,
        winnerVictories: isByeMatch ? 2 : Math.max(playerWins, opponentWins),
        winnerDefeats: 0,
        winnerDraws: 0,
        loserVictories: isByeMatch ? 0 : Math.min(playerWins, opponentWins),
        loserDefeats: 0,
        loserDraws: 0,
      })

      if (result?.status?.success) {
        const playerName = match.Player.Person.fullName
        const opponentName = match.Opponent?.Person.fullName || "BYE"

        const shortPlayerName = match.Player.Person.name
        const shortPpponentName = match.Opponent?.Person.name || "BYE"

        let bigResultText = ""
        let resultText = ""
        if (isByeMatch) {
          resultText = `${playerName} ganó por BYE`
          bigResultText = `GANA <br />POR BYE`
        } else if (isDraw) {
          resultText = `Empate ${playerWins}-${opponentWins} entre ${playerName} y ${opponentName}`
          bigResultText = `EMPATE <br />${playerWins}-${opponentWins}`
        } else {
          const winnerName = winnerId === match.playerId ? playerName : opponentName
          const showtWinnerName = winnerId === match.playerId ? shortPlayerName : shortPpponentName
          resultText = `${winnerName} ganó ${Math.max(playerWins, opponentWins)}-${Math.min(playerWins, opponentWins)}`
          bigResultText = `${showtWinnerName} <br />GANA <br />${Math.max(playerWins, opponentWins)}-${Math.min(playerWins, opponentWins)}`
        }

        toast({
          title: "Resultado reportado",
          description: resultText,
          variant: "success",
        })

        // Clear modal state
        setPlayerWins(0)
        setOpponentWins(0)
        setDropPlayer(false)
        setDropOpponent(false)

        onSuccess()
        if (isMobile) {
          setResultText(bigResultText)
          setShowResultModal(true)
        } else {
          onClose()
        }
      } else {
        toast({
          title: "Error",
          description: result?.status?.detail || "No se pudo reportar el resultado",
          variant: "destructive",
        })
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Error al reportar el resultado",
        variant: "destructive",
      })
    }
  }

  const handleCycleWins = (
    current: number,
    setter: React.Dispatch<React.SetStateAction<number>>,
  ) => {
    setter((current + 1) % 3) // 0 → 1 → 2 → 0
  }

  const handleSwapWins = () => {
    setPlayerWins(opponentWins)
    setOpponentWins(playerWins)
  }

  const isValidResult =
    isByeMatch ||
    (playerWins >= 0 &&
      playerWins <= 2 &&
      opponentWins >= 0 &&
      opponentWins <= 2 &&
      !(playerWins === 2 && opponentWins === 2))

  if (!match) return null

  return (
    <>
      <Dialog open={!!match} onOpenChange={onClose}>
        <DialogContent className="w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6 dark:bg-gray-800">
          <DialogHeader>
            <DialogTitle className="flex flex-col items-center gap-2">
              <div className="flex items-center gap-2 text-base sm:text-lg leading-tight">
                <Users className="h-4 w-4 shrink-0" />
                <span className="truncate">Mesa {match.table}</span>
              </div>
              {match.ModifiedBy && (
                <div>
                  <p className="text-sm">Reportado por: {match.ModifiedBy?.fullName}</p>
                  <span className="text-xs text-muted-foreground">
                    {new Date(match.modified).toLocaleDateString("es-ES", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                    })}
                  </span>
                </div>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Player Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Player 1 */}
              <Card>
                <CardContent className="p-2">
                  <div className="flex items-center gap-3 mb-4">
                    <UserAvatar
                      src={match.Player.Person.urlAvatar}
                      name={match.Player.Person.fullName}
                      size="sm"
                    />
                    <div className="flex-1">
                      <p className="font-medium">{match.Player.Person.fullName}</p>
                      <p className="text-sm text-muted-foreground">
                        Código: {match.Player.Person.code}
                      </p>
                      {match.Player.drop === 1 && (
                        <Badge variant="destructive" className="text-xs mt-1">
                          Drop
                        </Badge>
                      )}
                    </div>
                  </div>

                  {!isByeMatch && (
                    <>
                      <div className="hidden sm:inline grid grid-cols-2 gap-2 items-center">
                        <label className="text-sm font-medium">Partidas ganadas</label>
                        <Select
                          value={playerWins.toString()}
                          onValueChange={(value) => setPlayerWins(Number(value))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0">0</SelectItem>
                            <SelectItem value="1">1</SelectItem>
                            <SelectItem value="2">2</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="flex items-center space-x-2 mt-3">
                        <Checkbox
                          id="drop-player"
                          checked={dropPlayer}
                          onCheckedChange={(checked) => setDropPlayer(checked === true)}
                        />
                        <label htmlFor="drop-player" className="text-sm font-medium">
                          Marcar como Drop
                        </label>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Player 2 or BYE */}
              <Card>
                <CardContent className="p-2">
                  {isByeMatch ? (
                    <div className="flex items-center justify-center h-full min-h-[120px]">
                      <div className="text-center">
                        <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-2">
                          <span className="text-muted-foreground text-xs">BYE</span>
                        </div>
                        <p className="font-medium text-muted-foreground">Sin oponente</p>
                        <p className="text-sm text-muted-foreground">BYE automático</p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-3 mb-4">
                        <UserAvatar
                          src={match.Opponent.Person.urlAvatar}
                          name={match.Opponent.Person.fullName}
                          size="sm"
                        />
                        <div className="flex-1">
                          <p className="font-medium">{match.Opponent?.Person?.fullName}</p>
                          <p className="text-sm text-muted-foreground">
                            Código: {match.Opponent?.Person?.code}
                          </p>
                          {match.Opponent?.drop === 1 && (
                            <Badge variant="destructive" className="text-xs mt-1">
                              Drop
                            </Badge>
                          )}
                        </div>
                      </div>

                      <div className="grid hidden sm:inline grid-cols-2 gap-2 items-center">
                        <label className="text-sm font-medium">Partidas ganadas</label>
                        <Select
                          value={opponentWins.toString()}
                          onValueChange={(value) => setOpponentWins(Number(value))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0">0</SelectItem>
                            <SelectItem value="1">1</SelectItem>
                            <SelectItem value="2">2</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="flex items-center space-x-2 mt-3">
                        <Checkbox
                          id="drop-opponent"
                          checked={dropOpponent}
                          onCheckedChange={(checked) => setDropOpponent(checked === true)}
                        />
                        <label htmlFor="drop-opponent" className="text-sm font-medium">
                          Marcar como Drop
                        </label>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
            {/* Result Preview */}
            {!isByeMatch && (
              <Card className="bg-muted/50 border-2">
                <CardContent className="p-2">
                  <div className="flex items-center justify-center gap-8">
                    <div className="text-center">
                      <div
                        onClick={() => handleCycleWins(playerWins, setPlayerWins)}
                        className={`cursor-pointer select-none text-5xl font-black mb-2 px-4 py-2 rounded-lg ${
                          playerWins > opponentWins
                            ? "text-green-600 bg-green-50 dark:bg-green-950 border-2 border-green-200 dark:border-green-800"
                            : playerWins < opponentWins
                              ? "text-red-600 bg-red-50 dark:bg-red-950 border-2 border-red-200 dark:border-red-800"
                              : "text-muted-foreground bg-muted border-2 border-muted-foreground/20"
                        }`}
                      >
                        {playerWins}
                      </div>
                      <div className="text-muted-foreground grid">
                        <span className="font-bold">
                          {match.Player.Person.fullName.split(" ")[0]}
                        </span>
                        <span className="text-xs">{match.Player.Person.code}</span>
                      </div>
                    </div>

                    <div
                      onClick={handleSwapWins}
                      className="text-4xl font-bold text-muted-foreground cursor-pointer select-none hover:scale-110 transition"
                    >
                      VS
                    </div>

                    <div className="text-center">
                      <div
                        onClick={() => handleCycleWins(opponentWins, setOpponentWins)}
                        className={`cursor-pointer select-none text-5xl font-black mb-2 px-4 py-2 rounded-lg ${
                          opponentWins > playerWins
                            ? "text-green-600 bg-green-50 dark:bg-green-950 border-2 border-green-200 dark:border-green-800"
                            : opponentWins < playerWins
                              ? "text-red-600 bg-red-50 dark:bg-red-950 border-2 border-red-200 dark:border-red-800"
                              : "text-muted-foreground bg-muted border-2 border-muted-foreground/20"
                        }`}
                      >
                        {opponentWins}
                      </div>
                      <div className="text-muted-foreground grid">
                        <span className="font-bold">
                          {match.Opponent?.Person?.fullName?.split(" ")[0]}
                        </span>
                        <span className="text-xs">{match.Opponent?.Person?.code}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Validation Warning */}
            {!isValidResult && (
              <div className="flex items-center gap-2 p-3 bg-yellow-50 dark:bg-yellow-950 rounded-lg">
                <AlertTriangle className="h-4 w-4 text-yellow-600" />
                <span className="text-sm text-yellow-800 dark:text-yellow-200">
                  No se puede empatar 2-2. Los empates válidos son 0-0 o 1-1.
                </span>
              </div>
            )}
          </div>

          <DialogFooter className="grid grid-cols-[1fr_2fr] gap-4 sm:justify-end">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={loading}
              className="w-full sm:w-auto"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!isValidResult || loading || isByeMatch}
              className="w-full sm:w-auto gap-2"
            >
              <Trophy className="h-4 w-4" />
              {loading ? "Reportando..." : "Reportar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={showResultModal} onOpenChange={() => {}}>
        <DialogContent className="w-screen h-screen flex items-center justify-center bg-black text-white p-0">
          <div className="text-center space-y-8 px-6">
            <div className="text-5xl font-black tracking-widest">MESA {match?.table}</div>
            <div
              className="text-5xl font-black text-green-400"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(resultText) }}
            />
            <Button
              size="lg"
              className="text-xl px-10 py-6"
              onClick={() => {
                setShowResultModal(false)
                onSuccess()
                onClose()
              }}
            >
              OK
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
